import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents, VariantSelectedEvent } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';

/**
 * @typedef {Object} QuickAddFormulaProducts
 * @property {string | null} product1
 * @property {string | null} product2
 * @property {string | null} product3
 */

/**
 * @typedef {Object} ShopifyProductVariant
 * @property {number} id
 * @property {string} title
 * @property {string} option1
 * @property {string} [option2]
 * @property {string} [option3]
 * @property {number} price
 * @property {boolean} available
 * @property {string} [featured_image]
 * @property {string} [image]
 */

/**
 * @typedef {Object} ShopifySellingPlan
 * @property {number} id
 * @property {string} name
 * @property {string} [description]
 * @property {Array<{name: string, value: string}>} [options]
 * @property {Array<{value_type: string, value: number}>} [price_adjustments]
 */

/**
 * @typedef {Object} ShopifySellingPlanGroup
 * @property {number} id
 * @property {string} name
 * @property {ShopifySellingPlan[]} [selling_plans]
 */

/**
 * @typedef {Object} ShopifyProduct
 * @property {number} id
 * @property {string} handle
 * @property {string} title
 * @property {string[]} [images]
 * @property {string} [featured_image]
 * @property {ShopifyProductVariant[]} variants
 * @property {boolean} [available]
 * @property {any} [metafields]
 * @property {ShopifySellingPlanGroup[]} [selling_plan_groups]
 */

/**
 * @typedef {Object} Window
 * @property {QuickAddFormulaProducts | undefined} quickAddFormulaProducts
 * @property {string | null | undefined} quickAddSubscriptionBenefits
 * @property {any} jQuery
 * @property {any} Shopify
 */

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = productCard?.getProductCardLink() || hotspotProduct?.getHotspotProductLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const dialog = document.getElementById('quick-add-dialog');
    const dialogElement = dialog?.querySelector('dialog');
    const isDrawer = dialogElement?.classList.contains('dialog-drawer');

    if (isDrawer) {
      // Use custom drawer layout
      await this.#handleDrawerClick();
    } else {
      // Use existing modal functionality
      const currentUrl = this.productPageUrl;

      // Check if we have cached content for this URL
      let productGrid = this.#cachedContent.get(currentUrl);

      if (!productGrid) {
        // Fetch and cache the content
        const html = await this.fetchProductPage(currentUrl);
        if (html) {
          const gridElement = html.querySelector('[data-product-grid-content]');
          if (gridElement) {
            // Cache the cloned element to avoid modifying the original
            productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
            this.#cachedContent.set(currentUrl, productGrid);
          }
        }
      }

      if (productGrid) {
        // Use a fresh clone from the cache
        const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
        await this.updateQuickAddModal(freshContent);
      }
    }

    this.#openQuickAddModal();
  };

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      if (productPrice) {
        productHeader.appendChild(productPrice);
      }
      productGrid.appendChild(productHeader);

      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }
      if (productFormComponent) {
        productGrid.appendChild(productFormComponent);
      }

      productDetails?.remove();
    }

    morph(modalContent, productGrid);

    this.#syncVariantSelection(modalContent);
  }

  /**
   * Updates the quick-add button state based on whether a swatch is selected
   * @param {VariantSelectedEvent} event - The variant selected event
   */
  #updateQuickAddButtonState(event) {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    const productOptionsCount = this.dataset.productOptionsCount;
    const quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';
    this.setAttribute('data-quick-add-button', quickAddButton);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }

  /**
   * Handles drawer click - fetches product JSON and builds custom layout
   */
  async #handleDrawerClick() {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;

    // Get product handle from URL
    const productUrl = this.productPageUrl;
    const url = new URL(productUrl);
    const productHandle = url.pathname.split('/products/')[1]?.split('?')[0];

    if (!productHandle) return;

    // Fetch product JSON
    let productData = await this.#fetchProductJson(productHandle);
    if (!productData) return;

    // If metafields are missing, try to get them from the product page HTML
    if (!productData.metafields || !productData.metafields.reviews) {
      try {
        const html = await this.fetchProductPage(productUrl);
        if (html) {
          // Try to extract metafields from the HTML
          const productScripts = html.querySelectorAll('script[type="application/json"][data-product-json], script[data-product-json]');
          for (const script of productScripts) {
            try {
              const fullProductData = JSON.parse(script.textContent || '{}');
              // Handle both direct product object and wrapped structure
              const product = fullProductData.product || fullProductData;
              if (product && product.metafields) {
                productData.metafields = product.metafields;
                // console.log('Extracted metafields from HTML:', productData.metafields);
                break;
              }
            } catch (e) {
              // console.log('Error parsing product script:', e);
            }
          }
        }
      } catch (e) {
        // console.log('Could not fetch product page for metafields:', e);
      }
    }

    // Get formula products from global variable or data attributes
    let formulaHandles = /** @type {string[]} */ ([]);
    
    /** @type {QuickAddFormulaProducts | undefined} */
    const quickAddFormulaProducts = /** @type {any} */ (window).quickAddFormulaProducts;
    
    if (quickAddFormulaProducts) {
      const products = quickAddFormulaProducts;
      const handles = [
        products.product1,
        products.product2,
        products.product3,
      ].filter(handle => handle && handle !== 'null' && handle !== '');
      formulaHandles = /** @type {string[]} */ (handles);
    }
    
    // Fallback to data attributes if global variable not available
    if (formulaHandles.length === 0) {
      const dialog = document.getElementById('quick-add-dialog');
      if (dialog) {
        const handles = [
          dialog.dataset.formulaProduct1,
          dialog.dataset.formulaProduct2,
          dialog.dataset.formulaProduct3,
        ].filter(handle => handle && handle !== 'null' && handle !== '');
        formulaHandles = /** @type {string[]} */ (handles);
      }
    }

    // console.log('Formula handles from settings:', formulaHandles);
    // console.log('Global variable available:', !!quickAddFormulaProducts);

    // Fetch all formula products
    const formulaProducts = /** @type {ShopifyProduct[]} */ ((await Promise.all(
      formulaHandles.map((handle) => {
        if (!handle) return null;
        return this.#fetchProductJson(handle);
      })
    )).filter(Boolean));

    // console.log('Fetched formula products:', formulaProducts);

    // Build custom drawer content
    this.#buildDrawerContent(modalContent, productData, formulaProducts);
  }

  /**
   * Fetches product JSON data
   * @param {string} handle - Product handle
   * @returns {Promise<ShopifyProduct | null>}
   */
  async #fetchProductJson(handle) {
    if (!handle) return null;

    try {
      const shopify = /** @type {any} */ (window.Shopify);
      const root = shopify?.routes?.root || '/';
      
      // First try to fetch from custom JSON view that includes metafields
      let product = null;
      try {
        const customResponse = await fetch(`${root}products/${handle}?view=quick-add-json`);
        if (customResponse.ok) {
          const customData = await customResponse.json();
          if (customData && customData.id) {
            product = customData;
            // console.log('Fetched product from custom JSON view:', product);
          }
        }
      } catch (e) {
        // console.log('Custom JSON view not available, using standard API');
      }
      
      // Fallback to standard JSON API
      if (!product) {
        const response = await fetch(`${root}products/${handle}.js`);
        if (!response.ok) return null;
        product = /** @type {ShopifyProduct} */ (await response.json());
      }
      
      // If metafields are still missing, try to fetch from product page HTML
      if (!product.metafields || !product.metafields.reviews) {
        try {
          const htmlResponse = await fetch(`${root}products/${handle}`);
          if (htmlResponse.ok) {
            const htmlText = await htmlResponse.text();
            const parser = new DOMParser();
            const htmlDoc = parser.parseFromString(htmlText, 'text/html');
            
            // Try multiple selectors for product data
            const selectors = [
              'script[type="application/json"][data-product-json]',
              'script[data-product-json]',
              'script[type="application/json"]',
            ];
            
            for (const selector of selectors) {
              const scripts = htmlDoc.querySelectorAll(selector);
              for (const script of scripts) {
                try {
                  const data = JSON.parse(script.textContent || '{}');
                  // Check if this looks like product data
                  if (data && (data.id || data.handle || data.title)) {
                    if (data.metafields) {
                      product.metafields = data.metafields;
                      // console.log('Extracted metafields from HTML script tag:', product.metafields);
                      break;
                    }
                    // Also check if product is nested
                    if (data.product && data.product.metafields) {
                      product.metafields = data.product.metafields;
                      // console.log('Extracted metafields from nested product:', product.metafields);
                      break;
                    }
                  }
                } catch (e) {
                  // Continue to next script
                }
              }
              if (product.metafields) break;
            }
          }
        } catch (e) {
          // console.log('Could not fetch product page for metafields:', e);
        }
      }
      
      // console.log('Fetched product:', product);
      // console.log('Product metafields:', product.metafields);
      
      return product;
    } catch (error) {
      // console.error('Error fetching product JSON:', error);
      return null;
    }
  }

  /**
   * Builds the custom drawer content
   * @param {Element} container - Container element
   * @param {ShopifyProduct} currentProduct - Current product data
   * @param {ShopifyProduct[]} formulaProducts - Array of formula product data
   */
  #buildDrawerContent(container, currentProduct, formulaProducts) {
    // Clear container
    container.innerHTML = '';

    // Create drawer content structure
    const drawerContent = document.createElement('div');
    drawerContent.className = 'quick-add-drawer-content';
    drawerContent.dataset.productHandle = currentProduct.handle;
    drawerContent.dataset.productId = currentProduct.id.toString();

    // Build gallery with carousel
    const gallery = this.#buildGallery(currentProduct);
    drawerContent.appendChild(gallery);

    // Build product info
    const info = this.#buildProductInfo(currentProduct, formulaProducts);
    drawerContent.appendChild(info);

    container.appendChild(drawerContent);

    // Initialize Slick carousel
    this.#initDrawerCarousel(drawerContent);

    // Setup event handlers
    this.#setupDrawerHandlers(drawerContent, currentProduct, formulaProducts);
  }

  /**
   * Builds the gallery section with carousel
   * @param {ShopifyProduct} product - Product data
   * @returns {HTMLElement}
   */
  #buildGallery(product) {
    const gallery = document.createElement('div');
    gallery.className = 'quick-add-drawer__gallery';

    const carousel = document.createElement('div');
    carousel.className = 'quick-add-drawer__carousel';
    carousel.setAttribute('data-carousel', '');

    // Handle images - Shopify JSON API returns array of image URLs as strings
    /** @type {string[]} */
    let images = [];
    if (product.images && Array.isArray(product.images)) {
      images = product.images;
    } else if (product.featured_image) {
      images = [product.featured_image];
    }

    // If still no images, try to get from variants
    if (images.length === 0 && product.variants && product.variants.length > 0) {
      const variantImages = product.variants
        .map(/** @param {ShopifyProductVariant} v */ (v) => v.featured_image || v.image)
        .filter(/** @param {string | undefined} img */ (img) => Boolean(img));
      if (variantImages.length > 0) {
        images = /** @type {string[]} */ ([...new Set(variantImages)]); // Remove duplicates
      }
    }

    // console.log('Product images:', images);

    images.forEach(/** @param {string} image */ (image, /** @param {number} index */ index) => {
      const slide = document.createElement('div');
      slide.className = 'quick-add-drawer__slide';
      const img = document.createElement('img');
      // Shopify JSON API returns image URLs as strings
      img.src = image;
      img.alt = product.title || 'Product image';
      img.className = 'quick-add-drawer__image';
      img.loading = index === 0 ? 'eager' : 'lazy';
      
      // Add error handling for images
      img.onerror = function() {
        // console.error('Failed to load image:', img.src);
        this.style.display = 'none';
      };
      
      slide.appendChild(img);
      carousel.appendChild(slide);
    });

    gallery.appendChild(carousel);

    // Show dots if more than one image
    if (images.length > 1) {
      const dots = document.createElement('div');
      dots.className = 'quick-add-drawer__dots';
      dots.setAttribute('data-carousel-dots', '');
      gallery.appendChild(dots);
    } else {
      // Hide dots if only one image
      const dots = gallery.querySelector('[data-carousel-dots]');
      if (dots) {
        /** @type {HTMLElement} */ (dots).style.display = 'none';
      }
    }

    return gallery;
  }

  /**
   * Builds the product info section
   * @param {ShopifyProduct} product - Current product data
   * @param {ShopifyProduct[]} formulaProducts - Formula products
   * @returns {HTMLElement}
   */
  #buildProductInfo(product, formulaProducts) {
    const info = document.createElement('div');
    info.className = 'quick-add-drawer__info';

    // Title and Reviews container
    const titleContainer = document.createElement('div');
    titleContainer.className = 'title_container';

    // Title
    const title = document.createElement('h2');
    title.className = 'quick-add-drawer__title';
    title.textContent = product.title;
    titleContainer.appendChild(title);

    // Reviews and Formulation
    const reviewsAndFormulation = this.#buildReviewsAndFormulation(product);
    if (reviewsAndFormulation) {
      titleContainer.appendChild(reviewsAndFormulation);
    }

    info.appendChild(titleContainer);

    // Formula selection
    const formulaSection = this.#buildFormulaSelection(product, formulaProducts);
    info.appendChild(formulaSection);

    // Container selection (variants)
    const containerSection = this.#buildContainerSelection(product);
    info.appendChild(containerSection);

    // Purchase options
    const purchaseOptions = this.#buildPurchaseOptions(product);
    info.appendChild(purchaseOptions);

    // Actions (quantity + add to cart)
    const actions = this.#buildActions();
    info.appendChild(actions);

    // Benefits
    const benefits = this.#buildBenefits();
    info.appendChild(benefits);

    return info;
  }

  /**
   * Builds reviews and formulation section
   * @param {ShopifyProduct} product - Product data
   * @returns {HTMLElement | null}
   */
  #buildReviewsAndFormulation(product) {
    const container = document.createElement('div');
    container.className = 'quick-add-drawer__reviews-formulation';

    // Check if we have metafields (they might be in product.metafields)
    // Shopify JSON API structure might vary, so check multiple possible paths
    let rating = null;
    let ratingCount = null;
    let formulation = null;

    if (product.metafields) {
      // Based on review.liquid block structure:
      // - rating is at: product.metafields.reviews.rating.value.rating
      // - scale_max is at: product.metafields.reviews.rating.value.scale_max
      // - rating_count is at: product.metafields.reviews.rating_count (no .value)
      // - formulation is at: product.metafields.custom.product_formulation.value
      
      // console.log('Product metafields structure:', product.metafields);
      // console.log('Reviews metafield:', product.metafields.reviews);
      
      const ratingObj = product.metafields.reviews?.rating?.value;
      if (ratingObj) {
        // ratingObj can be an object with .rating and .scale_max, or just a number
        if (typeof ratingObj === 'object' && ratingObj !== null) {
          rating = ratingObj.rating || ratingObj;
        } else {
          rating = ratingObj;
        }
      }
      
      // rating_count might be directly accessible or have .value
      ratingCount = product.metafields.reviews?.rating_count?.value ||
                    product.metafields.reviews?.rating_count;
      
      formulation = product.metafields.custom?.product_formulation?.value ||
                    product.metafields.custom?.product_formulation;
    }

    // console.log('Reviews and formulation data:', { rating, ratingCount, formulation, metafields: product.metafields });

    // Only create container if we have at least one of these
    if (!rating && !formulation) {
      // console.log('No reviews or formulation found, returning null');
      return null;
    }

    // Reviews section
    if (rating) {
      const reviewsContainer = document.createElement('div');
      reviewsContainer.className = 'quick-add-drawer__reviews';

      // Stars
      const starsContainer = document.createElement('div');
      starsContainer.className = 'quick-add-drawer__stars';
      // Rating can be a number or an object with .rating and .scale_max
      let ratingValue = rating;
      let maxRating = 5;
      
      if (typeof rating === 'object' && rating !== null) {
        ratingValue = rating.rating || rating;
        maxRating = rating.scale_max || 5;
      } else if (typeof rating === 'number') {
        ratingValue = rating;
        maxRating = 5;
      }
      
      for (let i = 1; i <= maxRating; i++) {
        const star = document.createElement('span');
        star.className = 'star';
        if (i <= Math.floor(ratingValue)) {
          star.classList.add('star--filled');
        } else if (i === Math.ceil(ratingValue) && ratingValue % 1 >= 0.5) {
          star.classList.add('star--half');
        }
        star.textContent = '★';
        starsContainer.appendChild(star);
      }

      reviewsContainer.appendChild(starsContainer);

      // Review count
      if (ratingCount) {
        const count = document.createElement('span');
        count.className = 'quick-add-drawer__review-count';
        count.textContent = `(${ratingCount} Reviews)`;
        reviewsContainer.appendChild(count);
      }

      container.appendChild(reviewsContainer);
    }

    // Formulation
    if (formulation) {
      const formulationEl = document.createElement('div');
      formulationEl.className = 'quick-add-drawer__formulation';
      formulationEl.textContent = formulation;
      container.appendChild(formulationEl);
    }

    return container;
  }

  /**
   * Builds formula selection buttons
   * @param {ShopifyProduct} currentProduct - Current product
   * @param {ShopifyProduct[]} formulaProducts - Formula products
   */
  #buildFormulaSelection(currentProduct, formulaProducts) {
    const section = document.createElement('div');
    section.className = 'quick-add-drawer__formula-selection';

    const label = document.createElement('label');
    label.className = 'quick-add-drawer__label';
    label.setAttribute('data-formula-label', '');
    label.textContent = `Formula: ${currentProduct.title}`;
    section.appendChild(label);

    const buttons = document.createElement('div');
    buttons.className = 'quick-add-drawer__formula-buttons';
    buttons.setAttribute('data-formula-buttons', '');

    // Add current product
    const currentBtn = document.createElement('button');
    currentBtn.type = 'button';
    currentBtn.className = 'quick-add-drawer__formula-btn active';
    currentBtn.textContent = currentProduct.title;
    currentBtn.dataset.productHandle = currentProduct.handle;
    currentBtn.dataset.productId = currentProduct.id.toString();
    buttons.appendChild(currentBtn);

    // Add formula products (exclude current product)
    formulaProducts.forEach(/** @param {ShopifyProduct} formulaProduct */ (formulaProduct) => {
      if (!formulaProduct) return;
      
      // Skip if it's the current product
      const productHandle = formulaProduct.handle || formulaProduct.id?.toString();
      const currentHandle = currentProduct.handle || currentProduct.id?.toString();
      if (productHandle === currentHandle) return;

      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'quick-add-drawer__formula-btn';
      btn.textContent = formulaProduct.title || 'Product';
      btn.dataset.productHandle = productHandle;
      btn.dataset.productId = formulaProduct.id ? formulaProduct.id.toString() : '';
      if (formulaProduct.available === false) {
        btn.classList.add('unavailable');
      }
      buttons.appendChild(btn);
    });

    section.appendChild(buttons);
    return section;
  }

  /**
   * Builds container selection (variants)
   * @param {ShopifyProduct} product - Product data
   */
  #buildContainerSelection(product) {
    const section = document.createElement('div');
    section.className = 'quick-add-drawer__container-selection';

    const label = document.createElement('label');
    label.className = 'quick-add-drawer__label';
    label.setAttribute('data-container-label', '');
    
    // Get the first active variant option
    const firstVariant = product.variants && product.variants.length > 0 ? product.variants[0] : null;
    const defaultOption = firstVariant ? (firstVariant.option1 || 'Default') : 'Default';
    label.textContent = `Container: ${defaultOption}`;
    section.appendChild(label);

    const buttons = document.createElement('div');
    buttons.className = 'quick-add-drawer__variant-buttons';
    buttons.setAttribute('data-variant-buttons', '');

    // Group variants by option (assuming first option is container)
    /** @type {Record<string, ShopifyProductVariant[]>} */
    const variantsByOption = {};
    product.variants.forEach(/** @param {ShopifyProductVariant} variant */ (variant) => {
      const option = variant.option1 || 'Default';
      if (!variantsByOption[option]) {
        variantsByOption[option] = [];
      }
      variantsByOption[option].push(variant);
    });

    Object.keys(variantsByOption).forEach((option, index) => {
      const variantArray = variantsByOption[option];
      if (!variantArray || variantArray.length === 0) return;
      const variant = variantArray[0];
      if (!variant) return;
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = `quick-add-drawer__variant-btn ${index === 0 ? 'active' : ''}`;
      btn.textContent = option;
      btn.dataset.variantId = variant.id.toString();
      if (!variant.available) {
        btn.classList.add('unavailable');
      }
      buttons.appendChild(btn);
    });

    section.appendChild(buttons);
    return section;
  }

  /**
   * Builds purchase options (Subscribe vs One-time) dynamically from selling plans
   * @param {ShopifyProduct} product - Product data
   */
  #buildPurchaseOptions(product) {
    const section = document.createElement('div');
    section.className = 'quick-add-drawer__purchase-options';
    section.setAttribute('data-purchase-options', '');

    const firstVariant = product.variants[0];
    if (!firstVariant) return section;

    const originalPrice = firstVariant.price;
    const uniqueId = product.id.toString();

    // Extract selling plan data
    let frequenciesString = '';
    let frequencyPlanMap = '';
    let frequencyDiscountMap = '';
    let defaultDiscountedPrice = originalPrice;
    let defaultFrequency = '';
    let defaultDiscountPercentage = 0;

    if (product.selling_plan_groups && product.selling_plan_groups.length > 0) {
      for (const group of product.selling_plan_groups) {
        for (const plan of group.selling_plans || []) {
          let frequencyValue = '';
          let planDiscountPercentage = 0;
          let planDiscountedPrice = originalPrice;

          // Get discount from price_adjustments
          if (plan.price_adjustments && plan.price_adjustments.length > 0) {
            for (const adjustment of plan.price_adjustments) {
              if (adjustment.value_type === 'percentage') {
                planDiscountPercentage = adjustment.value;
                const discountAmount = Math.round(originalPrice * planDiscountPercentage / 100);
                planDiscountedPrice = originalPrice - discountAmount;
                break;
              } else if (adjustment.value_type === 'fixed_amount') {
                const discountAmount = adjustment.value;
                planDiscountedPrice = originalPrice - discountAmount;
                planDiscountPercentage = Math.round((discountAmount * 100) / originalPrice);
                break;
              } else if (adjustment.value_type === 'price') {
                planDiscountedPrice = adjustment.value;
                const discountAmount = originalPrice - planDiscountedPrice;
                planDiscountPercentage = Math.round((discountAmount * 100) / originalPrice);
                break;
              }
            }
          }

          // Check plan options for frequency
          if (plan.options && plan.options.length > 0) {
            for (const option of plan.options) {
              const optionNameLower = option.name?.toLowerCase() || '';
              if (optionNameLower.includes('delivery') || optionNameLower.includes('interval') || optionNameLower.includes('frequency')) {
                frequencyValue = (option.value || '').trim();
                break;
              }
            }
          }

          // If no options found, try extracting from plan name
          if (!frequencyValue && plan.name) {
            const planNameLower = plan.name.toLowerCase();
            if (planNameLower.includes('week') || planNameLower.includes('month') || planNameLower.includes('day')) {
              frequencyValue = plan.name.trim();
            }
          }

          if (frequencyValue) {
            if (!frequenciesString) {
              frequenciesString = frequencyValue;
              frequencyPlanMap = `${frequencyValue}:${plan.id}`;
              frequencyDiscountMap = `${frequencyValue}:${planDiscountPercentage}`;
              defaultDiscountedPrice = planDiscountedPrice;
              defaultFrequency = frequencyValue;
              defaultDiscountPercentage = planDiscountPercentage;
            } else if (!frequenciesString.includes(frequencyValue)) {
              frequenciesString += `,${frequencyValue}`;
              frequencyPlanMap += `|${frequencyValue}:${plan.id}`;
              frequencyDiscountMap += `|${frequencyValue}:${planDiscountPercentage}`;
            }
          }
        }
      }
    }

    // Hidden input for selling plan ID
    const sellingPlanInput = document.createElement('input');
    sellingPlanInput.type = 'hidden';
    sellingPlanInput.name = 'selling_plan';
    sellingPlanInput.id = `selling-plan-${uniqueId}-${product.id}`;
    sellingPlanInput.setAttribute('data-selling-plan-input', '');
    sellingPlanInput.value = '';
    section.appendChild(sellingPlanInput);

    // Subscribe & Save Option
    const subscribeLabel = document.createElement('label');
    subscribeLabel.className = 'subscription-option selected';
    subscribeLabel.setAttribute('for', `subscription-${uniqueId}-${product.id}`);

    const subscribeRadio = document.createElement('input');
    subscribeRadio.type = 'radio';
    subscribeRadio.name = `subscription-type-${uniqueId}-${product.id}`;
    subscribeRadio.id = `subscription-${uniqueId}-${product.id}`;
    subscribeRadio.value = 'subscription';
    subscribeRadio.checked = true;
    subscribeRadio.className = 'sr-only';
    subscribeRadio.setAttribute('data-subscription-radio', '');
    subscribeLabel.appendChild(subscribeRadio);

    const subscribeContent = document.createElement('div');
    subscribeContent.className = 'subscription-option__content';

    // Pricing row
    const pricingRow = document.createElement('div');
    pricingRow.className = 'subscription-option__row subscription-option__row-pricing';

    const subscribeTitle = document.createElement('h3');
    subscribeTitle.className = 'subscription-option__title';
    subscribeTitle.setAttribute('data-subscription-title', '');
    subscribeTitle.setAttribute('data-title-prefix', 'Subscribe & Save');
    subscribeTitle.setAttribute('data-default-discount', defaultDiscountPercentage.toString());
    subscribeTitle.textContent = `Subscribe & Save${defaultDiscountPercentage > 0 ? ` ${Math.round(defaultDiscountPercentage)}%` : ''}`;

    const subscribePricing = document.createElement('div');
    subscribePricing.className = 'subscription-option__pricing';
    subscribePricing.setAttribute('data-subscription-pricing', '');
    subscribePricing.setAttribute('data-original-price', originalPrice.toString());
    subscribePricing.setAttribute('data-frequency-discount-map', frequencyDiscountMap);

    const originalPriceEl = document.createElement('div');
    originalPriceEl.className = 'text-sm text-opacity line-through subscription-original-price';
    originalPriceEl.textContent = this.#formatPrice(originalPrice);

    const discountedPriceEl = document.createElement('div');
    discountedPriceEl.className = 'font-semibold subscription-discounted-price';
    discountedPriceEl.textContent = this.#formatPrice(defaultDiscountedPrice);

    subscribePricing.appendChild(originalPriceEl);
    subscribePricing.appendChild(discountedPriceEl);

    pricingRow.appendChild(subscribeTitle);
    pricingRow.appendChild(subscribePricing);
    subscribeContent.appendChild(pricingRow);

    // Benefits and delivery row
    const benefitsRow = document.createElement('div');
    benefitsRow.className = 'subscription-option__row subscription-option__row-benefits';

    const benefitsDiv = document.createElement('div');
    benefitsDiv.className = 'subscription-option__benefits';
    
    // Get subscription benefits from theme settings (preferred) or product metafields (fallback)
    /** @type {string | null | undefined} */
    const quickAddSubscriptionBenefits = /** @type {any} */ (window).quickAddSubscriptionBenefits;
    const subscriptionBenefits = quickAddSubscriptionBenefits || product.metafields?.custom?.subscription_benefits?.value;
    if (subscriptionBenefits) {
      benefitsDiv.innerHTML = subscriptionBenefits;
    }
    
    benefitsRow.appendChild(benefitsDiv);

    // Delivery frequency selector
    if (frequenciesString) {
      const deliveryDiv = document.createElement('div');
      deliveryDiv.className = 'subscription-option__delivery';

      const deliveryLabel = document.createElement('span');
      deliveryLabel.className = 'text-sm';
      deliveryLabel.textContent = 'Deliver Every';
      deliveryDiv.appendChild(deliveryLabel);

      const frequencySelect = document.createElement('select');
      frequencySelect.name = `delivery-frequency-${uniqueId}-${product.id}`;
      frequencySelect.className = 'subscription-option__frequency';
      frequencySelect.setAttribute('data-subscription-frequency-select', '');
      frequencySelect.setAttribute('data-product-id', product.id.toString());
      frequencySelect.setAttribute('data-default-frequency', defaultFrequency);
      frequencySelect.setAttribute('data-selling-plan-frequencies', frequenciesString);
      frequencySelect.setAttribute('data-frequency-plan-map', frequencyPlanMap);
      frequencySelect.setAttribute('data-frequency-discount-map', frequencyDiscountMap);
      frequencySelect.setAttribute('data-original-price', originalPrice.toString());

      const frequencies = frequenciesString.split(',');
      frequencies.forEach((freq) => {
        const freqTrimmed = freq.trim();
        const option = document.createElement('option');
        option.value = freqTrimmed;
        option.textContent = freqTrimmed;
        if (freqTrimmed === defaultFrequency.trim()) {
          option.selected = true;
        }
        frequencySelect.appendChild(option);
      });

      deliveryDiv.appendChild(frequencySelect);
      benefitsRow.appendChild(deliveryDiv);
    } else {
      const emptyDiv = document.createElement('div');
      benefitsRow.appendChild(emptyDiv);
    }

    subscribeContent.appendChild(benefitsRow);
    subscribeLabel.appendChild(subscribeContent);
    section.appendChild(subscribeLabel);

    // One Time Purchase Option
    const onetimeLabel = document.createElement('label');
    onetimeLabel.className = 'subscription-option';
    onetimeLabel.setAttribute('for', `one-time-${uniqueId}-${product.id}`);

    const onetimeRadio = document.createElement('input');
    onetimeRadio.type = 'radio';
    onetimeRadio.name = `subscription-type-${uniqueId}-${product.id}`;
    onetimeRadio.id = `one-time-${uniqueId}-${product.id}`;
    onetimeRadio.value = 'one-time';
    onetimeRadio.className = 'sr-only';
    onetimeRadio.setAttribute('data-subscription-radio', '');
    onetimeLabel.appendChild(onetimeRadio);

    const onetimeContent = document.createElement('div');
    onetimeContent.className = 'subscription-option__content';

    // Pricing row - title and price on one line
    const onetimePricingRow = document.createElement('div');
    onetimePricingRow.className = 'subscription-option__row subscription-option__row-pricing';

    const onetimeTitle = document.createElement('h3');
    onetimeTitle.className = 'subscription-option__title';
    onetimeTitle.textContent = 'One Time Purchase';

    const onetimePricing = document.createElement('div');
    onetimePricing.className = 'subscription-option__pricing';

    const onetimePriceEl = document.createElement('div');
    onetimePriceEl.className = 'font-semibold subscription-onetime-price';
    onetimePriceEl.textContent = this.#formatPrice(originalPrice);

    onetimePricing.appendChild(onetimePriceEl);
    onetimePricingRow.appendChild(onetimeTitle);
    onetimePricingRow.appendChild(onetimePricing);
    onetimeContent.appendChild(onetimePricingRow);
    onetimeLabel.appendChild(onetimeContent);
    section.appendChild(onetimeLabel);

    return section;
  }

  /**
   * Builds actions section (quantity + add to cart)
   */
  #buildActions() {
    const section = document.createElement('div');
    section.className = 'quick-add-drawer__actions';

    const quantity = document.createElement('div');
    quantity.className = 'quick-add-drawer__quantity';
    const decreaseBtn = document.createElement('button');
    decreaseBtn.type = 'button';
    decreaseBtn.className = 'quick-add-drawer__qty-btn';
    decreaseBtn.setAttribute('data-qty-decrease', '');
    decreaseBtn.textContent = '-';
    const qtyInput = document.createElement('input');
    qtyInput.type = 'number';
    qtyInput.value = '1';
    qtyInput.min = '1';
    qtyInput.className = 'quick-add-drawer__qty-input';
    qtyInput.setAttribute('data-quantity', '');
    const increaseBtn = document.createElement('button');
    increaseBtn.type = 'button';
    increaseBtn.className = 'quick-add-drawer__qty-btn';
    increaseBtn.setAttribute('data-qty-increase', '');
    increaseBtn.textContent = '+';
    quantity.appendChild(decreaseBtn);
    quantity.appendChild(qtyInput);
    quantity.appendChild(increaseBtn);

    const addToCart = document.createElement('button');
    addToCart.type = 'button';
    addToCart.className = 'button button--primary quick-add-drawer__add-to-cart';
    addToCart.setAttribute('data-add-to-cart', '');
    addToCart.textContent = 'Add to Bag';

    section.appendChild(quantity);
    section.appendChild(addToCart);

    return section;
  }

  /**
   * Builds benefits section
   */
  #buildBenefits() {
    const section = document.createElement('div');
    section.className = 'quick-add-drawer__benefits';

    // Get benefits from theme settings (preferred) or use default
    /** @type {string | null | undefined} */
    const drawerBenefits = /** @type {any} */ (window).quickAddDrawerBenefits;
    
    if (drawerBenefits) {
      section.innerHTML = drawerBenefits;
    } else {
      // Fallback to default benefits
      const benefit1 = document.createElement('div');
      benefit1.className = 'quick-add-drawer__benefit';
      benefit1.innerHTML = '<span>Free Shipping on orders of $30+</span>';

      const benefit2 = document.createElement('div');
      benefit2.className = 'quick-add-drawer__benefit';
      benefit2.innerHTML = '<span>Money back Guarantee</span>';

      section.appendChild(benefit1);
      section.appendChild(benefit2);
    }

    return section;
  }

  /**
   * Formats price
   * @param {number} cents - Price in cents
   */
  #formatPrice(cents) {
    return `$${(cents / 100).toFixed(2)}`;
  }

  /**
   * Initializes Slick carousel for drawer
   * @param {Element} container - Container element
   */
  #initDrawerCarousel(container) {
    const jQuery = /** @type {any} */ (window).jQuery;
    if (typeof jQuery === 'undefined' || typeof jQuery.fn.slick === 'undefined') {
      setTimeout(() => this.#initDrawerCarousel(container), 100);
      return;
    }

    const $ = jQuery;
    const carousel = container.querySelector('[data-carousel]');
    if (!carousel) return;

    const slides = carousel.querySelectorAll('.quick-add-drawer__slide');
    if (slides.length <= 1) {
      // Hide dots if only one slide
      const dots = container.querySelector('[data-carousel-dots]');
      if (dots) {
        /** @type {HTMLElement} */ (dots).style.display = 'none';
      }
      return;
    }

    // Show dots
    const dots = container.querySelector('[data-carousel-dots]');
    if (dots) {
      /** @type {HTMLElement} */ (dots).style.display = 'flex';
    }

    // Destroy existing Slick if present
    if ($(carousel).hasClass('slick-initialized')) {
      $(carousel).slick('unslick');
    }

    // Wait a bit for images to load
    setTimeout(() => {
      const dotsContainer = container.querySelector('[data-carousel-dots]');
      $(carousel).slick({
        dots: true,
        arrows: true,
        infinite: true,
        speed: 300,
        slidesToShow: 1,
        slidesToScroll: 1,
        adaptiveHeight: true,
        fade: false,
        appendDots: dotsContainer || container,
        prevArrow: '<button type="button" class="slick-prev" aria-label="Previous">‹</button>',
        nextArrow: '<button type="button" class="slick-next" aria-label="Next">›</button>',
        centerMode: false,
        variableWidth: false,
      });

      // Ensure dots are visible after initialization
      setTimeout(() => {
        const slickDots = container.querySelector('.slick-dots');
        if (slickDots) {
          const dotsEl = /** @type {HTMLElement} */ (slickDots);
          dotsEl.style.display = 'flex';
          dotsEl.style.visibility = 'visible';
          dotsEl.style.opacity = '1';
          
          // Also ensure the parent container is visible
          if (dotsContainer) {
            /** @type {HTMLElement} */ (dotsContainer).style.display = 'flex';
            /** @type {HTMLElement} */ (dotsContainer).style.visibility = 'visible';
            /** @type {HTMLElement} */ (dotsContainer).style.opacity = '1';
          }
          
          // Ensure all dot buttons are visible
          const dotButtons = slickDots.querySelectorAll('li button');
          dotButtons.forEach(/** @param {HTMLElement} btn */ (btn) => {
            btn.style.display = 'block';
            btn.style.visibility = 'visible';
            btn.style.opacity = '1';
          });
        }
        $(carousel).slick('setPosition');
      }, 100);
    }, 50);
  }

  /**
   * Sets up event handlers for drawer
   * @param {Element} container - Container element
   * @param {ShopifyProduct} currentProduct - Current product
   * @param {ShopifyProduct[]} formulaProducts - Formula products
   */
  #setupDrawerHandlers(container, currentProduct, formulaProducts) {
    // Formula switching
    const formulaButtons = container.querySelectorAll('[data-formula-buttons] button');
    formulaButtons.forEach(/** @param {HTMLElement} btn */ (btn) => {
      btn.addEventListener('click', async () => {
        if (btn.classList.contains('unavailable')) return;

        const handle = btn.dataset.productHandle;
        if (!handle) return;

        // Update active state
        formulaButtons.forEach(/** @param {HTMLElement} b */ (b) => b.classList.remove('active'));
        btn.classList.add('active');

        // Update formula label
        const formulaLabel = container.querySelector('[data-formula-label]');
        if (formulaLabel) {
          formulaLabel.textContent = `Formula: ${btn.textContent}`;
        }

        // Store currently selected variant option before switching
        const currentVariantBtn = /** @type {HTMLElement | null} */ (container.querySelector('[data-variant-buttons] .active'));
        const selectedVariantOption = currentVariantBtn?.textContent?.trim() || null;

        // Fetch new product data
        const newProduct = await this.#fetchProductJson(handle);
        if (!newProduct) return;

        // Update product title
        this.#updateProductTitle(container, newProduct);

        // Update container variants (preserving selection if possible)
        this.#updateContainerVariants(container, newProduct, selectedVariantOption);

        // Update prices
        this.#updatePrices(container, newProduct);

        // Update gallery
        this.#updateGallery(container, newProduct);

        // Update product ID
        /** @type {HTMLElement} */ (container).dataset.productId = newProduct.id.toString();
        /** @type {HTMLElement} */ (container).dataset.productHandle = newProduct.handle;
      });
    });

    // Variant selection
    const variantButtons = container.querySelectorAll('[data-variant-buttons] button');
    variantButtons.forEach(/** @param {HTMLElement} btn */ (btn) => {
      btn.addEventListener('click', () => {
        if (btn.classList.contains('unavailable')) return;
        variantButtons.forEach(/** @param {HTMLElement} b */ (b) => b.classList.remove('active'));
        btn.classList.add('active');
        
        // Update container label
        const containerLabel = container.querySelector('[data-container-label]');
        if (containerLabel) {
          containerLabel.textContent = `Container: ${btn.textContent}`;
        }
        
        this.#updatePrices(container, currentProduct, btn.dataset.variantId);
      });
    });

    // Quantity controls
    const decreaseBtn = container.querySelector('[data-qty-decrease]');
    const increaseBtn = container.querySelector('[data-qty-increase]');
    const qtyInput = /** @type {HTMLInputElement | null} */ (container.querySelector('[data-quantity]'));

    decreaseBtn?.addEventListener('click', () => {
      if (!qtyInput) return;
      const current = parseInt(qtyInput.value) || 1;
      if (current > 1) {
        qtyInput.value = (current - 1).toString();
      }
    });

    increaseBtn?.addEventListener('click', () => {
      if (!qtyInput) return;
      const current = parseInt(qtyInput.value) || 1;
      qtyInput.value = (current + 1).toString();
    });

    // Subscription radio buttons change
    const subscriptionRadios = container.querySelectorAll('[data-subscription-radio]');
    subscriptionRadios.forEach(/** @param {HTMLElement} radio */ (radio) => {
      radio.addEventListener('change', () => {
        // Update selected class on labels
        const labels = container.querySelectorAll('.subscription-option');
        labels.forEach(label => label.classList.remove('selected'));
        const selectedLabel = radio.closest('.subscription-option');
        if (selectedLabel) {
          selectedLabel.classList.add('selected');
        }
        this.#updatePrices(container, currentProduct);
      });
    });

    // Frequency select change
    const frequencySelect = /** @type {HTMLSelectElement | null} */ (container.querySelector('[data-subscription-frequency-select]'));
    if (frequencySelect) {
      frequencySelect.addEventListener('change', () => {
        this.#updateSubscriptionPricing(container, currentProduct, frequencySelect);
      });
    }

    // Add to cart
    const addToCartBtn = container.querySelector('[data-add-to-cart]');
    addToCartBtn?.addEventListener('click', () => {
      this.#handleDrawerAddToCart(container);
    });
  }

  /**
   * Updates product title when product changes
   * @param {Element} container - Container element
   * @param {ShopifyProduct} product - Product data
   */
  #updateProductTitle(container, product) {
    const title = container.querySelector('.quick-add-drawer__title');
    if (title) {
      title.textContent = product.title;
    }

    // Update reviews and formulation
    const reviewsFormulation = container.querySelector('.quick-add-drawer__reviews-formulation');
    if (reviewsFormulation) {
      reviewsFormulation.remove();
    }
    const newReviewsFormulation = this.#buildReviewsAndFormulation(product);
    if (newReviewsFormulation) {
      const titleEl = container.querySelector('.quick-add-drawer__title');
      if (titleEl && titleEl.parentNode) {
        if (titleEl.nextSibling) {
          titleEl.parentNode.insertBefore(newReviewsFormulation, titleEl.nextSibling);
        } else {
          titleEl.parentNode.appendChild(newReviewsFormulation);
        }
      }
    }
  }

  /**
   * Updates container variants when product changes
   * @param {Element} container - Container element
   * @param {ShopifyProduct} product - Product data
   * @param {string | null} [preserveOption] - Option name to preserve if it exists in new product
   */
  #updateContainerVariants(container, product, preserveOption = null) {
    const variantButtons = container.querySelector('[data-variant-buttons]');
    if (!variantButtons) return;

    variantButtons.innerHTML = '';

    /** @type {Record<string, ShopifyProductVariant[]>} */
    const variantsByOption = {};
    product.variants.forEach(/** @param {ShopifyProductVariant} variant */ (variant) => {
      const option = variant.option1 || 'Default';
      if (!variantsByOption[option]) {
        variantsByOption[option] = [];
      }
      variantsByOption[option].push(variant);
    });

    // Check if preserved option exists in new product
    const optionKeys = Object.keys(variantsByOption);
    const optionToSelect = preserveOption && optionKeys.includes(preserveOption) 
      ? preserveOption 
      : (optionKeys[0] || null);

    let selectedOption = null;
    Object.keys(variantsByOption).forEach((option, index) => {
      const variantArray = variantsByOption[option];
      if (!variantArray || variantArray.length === 0) return;
      const variant = variantArray[0];
      if (!variant) return;
      const btn = document.createElement('button');
      btn.type = 'button';
      const isSelected = option === optionToSelect;
      btn.className = `quick-add-drawer__variant-btn ${isSelected ? 'active' : ''}`;
      btn.textContent = option;
      btn.dataset.variantId = variant.id.toString();
      if (!variant.available) {
        btn.classList.add('unavailable');
      }
      variantButtons.appendChild(btn);
      if (isSelected) {
        selectedOption = option;
      }
    });

    // Update container label with selected option
    const containerLabel = container.querySelector('[data-container-label]');
    if (containerLabel && selectedOption) {
      containerLabel.textContent = `Container: ${selectedOption}`;
    }

    // Re-attach event handlers
    const newButtons = variantButtons.querySelectorAll('button');
    newButtons.forEach(/** @param {HTMLElement} btn */ (btn) => {
      btn.addEventListener('click', () => {
        if (btn.classList.contains('unavailable')) return;
        newButtons.forEach(/** @param {HTMLElement} b */ (b) => b.classList.remove('active'));
        btn.classList.add('active');
        
        // Update container label
        const containerLabel = container.querySelector('[data-container-label]');
        if (containerLabel) {
          containerLabel.textContent = `Container: ${btn.textContent}`;
        }
        
        this.#updatePrices(container, product, btn.dataset.variantId);
      });
    });
  }

  /**
   * Updates subscription pricing when frequency changes
   * @param {Element} container - Container element
   * @param {ShopifyProduct} product - Product data
   * @param {HTMLSelectElement} frequencySelect - Frequency select element
   */
  #updateSubscriptionPricing(container, product, frequencySelect) {
    const selectedVariantBtn = /** @type {HTMLElement | null} */ (container.querySelector('[data-variant-buttons] .active'));
    const variantIdToUse = selectedVariantBtn?.dataset.variantId;
    const variant = product.variants.find(/** @param {ShopifyProductVariant} v */ (v) => v.id.toString() === variantIdToUse) || product.variants[0];
    if (!variant) return;

    const originalPrice = parseInt(frequencySelect.getAttribute('data-original-price') || variant.price.toString());
    const frequencyDiscountMap = frequencySelect.getAttribute('data-frequency-discount-map') || '';
    const frequencyPlanMap = frequencySelect.getAttribute('data-frequency-plan-map') || '';
    const selectedFrequency = /** @type {HTMLSelectElement} */ (frequencySelect).value;

    // Find discount for selected frequency
    let discountPercentage = 0;
    let sellingPlanId = '';
    const discountMapEntries = frequencyDiscountMap.split('|');
    const planMapEntries = frequencyPlanMap.split('|');

    for (let i = 0; i < discountMapEntries.length; i++) {
      const entry = discountMapEntries[i];
      if (!entry) continue;
      const parts = entry.split(':');
      const freq = parts[0];
      const discount = parts[1];
      if (freq && freq.trim() === selectedFrequency.trim()) {
        discountPercentage = discount ? parseFloat(discount) || 0 : 0;
        const planEntry = planMapEntries[i];
        if (planEntry) {
          const planParts = planEntry.split(':');
          sellingPlanId = planParts[1] || '';
        }
        break;
      }
    }

    // Update selling plan input
    const sellingPlanInput = /** @type {HTMLInputElement | null} */ (container.querySelector('[data-selling-plan-input]'));
    if (sellingPlanInput) {
      sellingPlanInput.value = sellingPlanId;
    }

    // Calculate discounted price
    const discountAmount = Math.round(originalPrice * discountPercentage / 100);
    const discountedPrice = originalPrice - discountAmount;

    // Update pricing display
    const pricingContainer = container.querySelector('[data-subscription-pricing]');
    if (pricingContainer) {
      const originalPriceEl = pricingContainer.querySelector('.subscription-original-price');
      const discountedPriceEl = pricingContainer.querySelector('.subscription-discounted-price');
      if (originalPriceEl) originalPriceEl.textContent = this.#formatPrice(originalPrice);
      if (discountedPriceEl) discountedPriceEl.textContent = this.#formatPrice(discountedPrice);
    }

    // Update title with discount percentage
    const titleEl = container.querySelector('[data-subscription-title]');
    if (titleEl) {
      const titlePrefix = titleEl.getAttribute('data-title-prefix') || 'Subscribe & Save';
      titleEl.textContent = `${titlePrefix}${discountPercentage > 0 ? ` ${Math.round(discountPercentage)}%` : ''}`;
    }
  }

  /**
   * Updates prices based on selected variant and purchase type
   * @param {Element} container - Container element
   * @param {ShopifyProduct} product - Product data
   * @param {string | null} [variantId] - Variant ID
   */
  #updatePrices(container, product, variantId = null) {
    const selectedVariantBtn = /** @type {HTMLElement | null} */ (container.querySelector('[data-variant-buttons] .active'));
    const variantIdToUse = variantId || (selectedVariantBtn?.dataset.variantId);
    const variant = product.variants.find(/** @param {ShopifyProductVariant} v */ (v) => v.id.toString() === variantIdToUse) || product.variants[0];

    if (!variant) return;

    const subscriptionRadio = /** @type {HTMLInputElement | null} */ (container.querySelector('[data-subscription-radio]:checked'));
    const purchaseType = subscriptionRadio?.value || 'subscription';
    const isSubscription = purchaseType === 'subscription';

    if (isSubscription) {
      // Update subscription pricing
      const frequencySelect = /** @type {HTMLSelectElement | null} */ (container.querySelector('[data-subscription-frequency-select]'));
      if (frequencySelect) {
        // Update original price in frequency select
        frequencySelect.setAttribute('data-original-price', variant.price.toString());
        this.#updateSubscriptionPricing(container, product, frequencySelect);
      }
    } else {
      // Update one-time price
      const onetimePrice = container.querySelector('.subscription-onetime-price');
      if (onetimePrice) {
        onetimePrice.textContent = this.#formatPrice(variant.price);
      }
    }
  }

  /**
   * Updates gallery when product changes
   * @param {Element} container - Container element
   * @param {ShopifyProduct} product - Product data
   */
  #updateGallery(container, product) {
    const carousel = container.querySelector('[data-carousel]');
    if (!carousel) return;

    // Destroy existing Slick
    const jQuery = /** @type {any} */ (window).jQuery;
    if (typeof jQuery !== 'undefined' && typeof jQuery.fn.slick !== 'undefined') {
      const $ = jQuery;
      if ($(carousel).hasClass('slick-initialized')) {
        $(carousel).slick('unslick');
      }
    }

    // Update slides
    carousel.innerHTML = '';
    
    // Handle images - Shopify JSON API returns array of image URLs as strings
    /** @type {string[]} */
    let images = [];
    if (product.images && Array.isArray(product.images)) {
      images = product.images;
    } else if (product.featured_image) {
      images = [product.featured_image];
    }

    // If still no images, try to get from variants
    if (images.length === 0 && product.variants && product.variants.length > 0) {
      const variantImages = product.variants
        .map(/** @param {ShopifyProductVariant} v */ (v) => v.featured_image || v.image)
        .filter(/** @param {string | undefined} img */ (img) => Boolean(img));
      if (variantImages.length > 0) {
        images = /** @type {string[]} */ ([...new Set(variantImages)]); // Remove duplicates
      }
    }

    images.forEach(/** @param {string} image */ (image, /** @param {number} index */ index) => {
      const slide = document.createElement('div');
      slide.className = 'quick-add-drawer__slide';
      const img = document.createElement('img');
      // Shopify JSON API returns image URLs as strings
      img.src = image;
      img.alt = product.title || 'Product image';
      img.className = 'quick-add-drawer__image';
      img.loading = index === 0 ? 'eager' : 'lazy';
      
      // Add error handling for images
      img.onerror = function() {
        // console.error('Failed to load image:', img.src);
        this.style.display = 'none';
      };
      
      slide.appendChild(img);
      carousel.appendChild(slide);
    });

    // Re-initialize Slick if more than one image
    if (images.length > 1) {
      setTimeout(() => {
        this.#initDrawerCarousel(container);
      }, 100);
    }
  }

  /**
   * Handles add to cart from drawer
   * @param {Element} container - Container element
   */
  async #handleDrawerAddToCart(container) {
    const containerEl = /** @type {HTMLElement} */ (container);
    const productId = containerEl.dataset.productId;
    const selectedVariantBtn = container.querySelector('[data-variant-buttons] .active');
    const variantId = selectedVariantBtn ? /** @type {HTMLElement} */ (selectedVariantBtn).dataset.variantId : undefined;
    const qtyInput = /** @type {HTMLInputElement | null} */ (container.querySelector('[data-quantity]'));
    const quantity = qtyInput ? parseInt(qtyInput.value) || 1 : 1;
    const subscriptionRadio = /** @type {HTMLInputElement | null} */ (container.querySelector('[data-subscription-radio]:checked'));
    const purchaseType = subscriptionRadio ? subscriptionRadio.value : 'subscription';

    if (!variantId) {
      // console.error('No variant selected');
      return;
    }

    // Build form data
    /** @type {{ items: Array<{ id: string; quantity: number; selling_plan?: string }> }} */
    const formData = {
      items: [
        {
          id: variantId,
          quantity: quantity,
        },
      ],
    };

    // Handle subscription if selected
    if (purchaseType === 'subscription') {
      const sellingPlanInput = /** @type {HTMLInputElement | null} */ (container.querySelector('[data-selling-plan-input]'));
      const sellingPlanId = sellingPlanInput ? sellingPlanInput.value : '';
      if (sellingPlanId && formData.items[0]) {
        formData.items[0].selling_plan = sellingPlanId;
      }
    }

    try {
      const shopify = /** @type {any} */ (window.Shopify);
      const root = shopify?.routes?.root || '/';
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error('Failed to add to cart');
      }

      const cartData = await response.json();

      // Close quick-add drawer
      const quickAddDialog = document.getElementById('quick-add-dialog');
      if (quickAddDialog instanceof QuickAddDialog) {
        quickAddDialog.closeDialog();
      }

      // Trigger cart update event
      document.dispatchEvent(
        new CustomEvent(ThemeEvents.cartUpdate, {
          detail: { data: cartData },
        })
      );

      // Open cart drawer
      setTimeout(() => {
        const cartDrawer = document.querySelector('cart-drawer-component');
        if (cartDrawer && 'open' in cartDrawer && typeof cartDrawer.open === 'function') {
          cartDrawer.open();
        } else {
          // Fallback: dispatch cart add event which should trigger cart drawer
          document.dispatchEvent(new CustomEvent('cart:add', { bubbles: true }));
          
          // Also try to find and click cart icon
          const cartIcon = /** @type {HTMLElement | null} */ (document.querySelector('[href*="/cart"], [data-cart-drawer], .cart-icon, [aria-label*="cart" i]'));
          if (cartIcon && 'click' in cartIcon && typeof cartIcon.click === 'function') {
            cartIcon.click();
          }
        }
      }, 100);
    } catch (error) {
      // console.error('Error adding to cart:', error);
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
